class CustomSelect extends HTMLElement {
  static observedAttributes = ["size-type"];

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.isOpen = false;
    this.valueMod = 0;
    this.optionType = this.getAttribute('option-type');
    this.label = this.getAttribute('label') || 'Select';
    this.selectedValue = this.getAttribute('selected-value') || '';
    this.selectedLabel = this.getAttribute('selected-label') || '';
    this.options = [];

    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.handleOptionClick = this.handleOptionClick.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  connectedCallback() {
    this.render();
    this.parseOptions();
    this.setupEventListeners();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name == "size-type") {
      this.convertSizes(newValue);
    }
  }

  getOptionElement(value) {
    return this.shadowRoot.querySelector(`.select-option[data-value="${value}"]`);
  }

  getOptionObject(value) {
    return this.options.find(option => option.value === value);
  }

  render() {
    const styles = `
      :host {
        display: flex;
        align-items: center;
        gap: 20px;
        position: relative;
      }

      :host::before {
        content: '';
        display: block;
        width: 2px;
        height: 80%;
        background: #EAEAEA;
      }

      .select-trigger {
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        user-select: none;
        gap: 10px;
      }

      .select-label {
        color: #0A0A0A;
        font-family: "GT America";
        font-size: 10.6px;
        font-style: normal;
        font-weight: 500;
        line-height: 24.08px;
        letter-spacing: 1.056px;
        text-transform: uppercase;
      }

      .select-value {
        color: #0A0A0A;
        font-family: "GT America";
        font-size: 12px;
        font-style: normal;
        font-weight: 300;
        line-height: 14px;
      }

      .select-value:empty {
        display: none;
      }

      .select-arrow {
        display: flex;
        align-items: center;
        transition: transform 0.2s ease;
      }

      :host(.open) .select-arrow svg {
        transform: rotate(180deg);
      }

      .select-options {
        position: absolute;
        bottom: 100%;
        left: 50%;
        right: 0;
        transform: translateX(-50%);
        background-color: white;
        margin-bottom: 4px;
        max-height: 200px;
        overflow-y: auto;
        z-index: 10;
        display: none;
      }

      .select-options.horizontal {
        width: fit-content;
        padding: 5px 18px;
        gap: 5px;
      }

      .select-options.vertical {
        transform: none;
        align-items: center;
        flex-direction: column;
        padding: 15px;
        gap: 15px;
      }

      .select-options.color {
        width: 15px;
        left: 40%;
      }

      :host(.open) .select-options {
        display: block;
      }
      
      :host(.open) .select-options.horizontal,
      :host(.open) .select-options.vertical {
        display: flex;
      }

      .select-option {
        color: #0A0A0A;
        font-family: "GT America";
        font-size: 12px;
        font-style: normal;
        font-weight: 300;
        line-height: 24px;
        padding: 0 5px;
        cursor: pointer;
      }

      .select-option.selected {
        font-weight: bold;
      }

      .select-option.disabled {
        color: #aaaaaa;
        // cursor: not-allowed;
        text-decoration: line-through;
      }

      .select-option .color-label {
        display: none;
      }

      .select-options.color .select-option.disabled,
      .select-options.color .select-option.selected {
        display: none;
      }

      .color-wrapper {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .color-box {
        display: block;
        width: 1.3rem;
        height: 1.3rem;
        border-radius: 50%;
        position: relative;
      }

      .select-value .color-box:after,
      .selected .color-box:after {
        content: "";
        display: block;
        width: 1.9rem;
        height: 1.9rem;
        border: 0.1rem solid rgb(0, 0, 0);
        border-radius: 50%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      @media (max-width: 768px) {
        .select-options.horizontal,
        .select-options.vertical {
          width: fit-content;
          max-width: 70vw;
          flex-direction: row;
          left: 0;
          bottom: 0;
          transform: translateY(110%);
          padding-left: 0;
        }
      }
    `;

    this.shadowRoot.innerHTML = `
      <style>${styles}</style>
      <div class="select-trigger">
        <span class="select-label">${this.label}</span>
        <span class="select-value">${this.selectedLabel}</span>
        <span class="select-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" width="9" height="7" viewBox="0 0 9 7" fill="none">
            <path d="M8.70898 1.94218L7.93898 1.24658C7.93898 1.24658 5.79647 3.2145 4.89921 4.19847H4.77586C3.752 3.21385 1.63416 1.24658 1.63416 1.24658L0.708984 1.94218L4.83689 5.75072L8.70898 1.94218Z" fill="black"/>
          </svg>
        </span>
      </div>
      <div class="select-options"></div>
    `;
  }

  parseOptions() {
    const optionsContainer = this.shadowRoot.querySelector('.select-options');
    const optionSlots = this.querySelectorAll('custom-option');

    if (this.optionType === 'color') {
      optionsContainer.classList.add('vertical');
    } else {
      optionsContainer.classList.add('horizontal');
    }

    optionsContainer.classList.add(this.optionType);

    optionSlots.forEach(option => {
      const value = option.getAttribute('value');
      const label = option.textContent.trim();
      const disabled = option.hasAttribute('disabled');
      const selected = option.hasAttribute('selected');
      const color = option.getAttribute('color') || null;

      const optionObject = {
        value, label, disabled, selected
      }

      if (this.optionType === 'color') {
        optionObject.color = color;
      }

      this.options.push(optionObject);


      const optionElement = document.createElement('div');
      optionElement.className = 'select-option';

      if (selected) {
        optionElement.classList.add('selected');
        this.setSelectedLabel(label, color, value);
        this.selectedValue = value;
        this.updateDisplay();
      }

      if (disabled) {
        optionElement.classList.add('disabled');
      }

      if (this.optionType === 'color') {
        const colorBox = this.renderColorBox(color, label);
        optionElement.appendChild(colorBox);
      } else {
        optionElement.textContent = label;
      }

      optionElement.dataset.value = value;

      // if (!disabled) {
      optionElement.addEventListener('click', () => this.handleOptionClick(value, label, color));
      // }

      optionsContainer.appendChild(optionElement);
    });
  }

  setSelectedLabel(label, color, value) {
    if (this.optionType === 'color') {
      this.selectedLabel = this.renderColorBox(color, label);
    } else {
      if (isNaN(parseInt(value))) {
        this.selectedLabel = String(label);
      } else {
        this.selectedLabel = String(parseInt(value, 10) + this.valueMod);
      }
    }
  }

  setupEventListeners() {
    const trigger = this.shadowRoot.querySelector('.select-trigger');
    trigger.addEventListener('click', this.toggleDropdown);
    document.addEventListener('click', this.handleClickOutside);
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.classList.add('open');

      const buttonContainer = document.querySelector('sticky-add-to-cart').shadowRoot.querySelector('.button-container');
      const dropdown = this.shadowRoot.querySelector('.select-options');

      if (buttonContainer && window.innerWidth < 768) {
        const dropdownHeight = dropdown.offsetHeight;
        buttonContainer.style.paddingTop = `${dropdownHeight}px`;
      }

      // Position the dropdown to the left
      const currentDropdownPositionX = dropdown.getBoundingClientRect().x;
      const leftPadding = 16;
      const offsetPositionX = leftPadding - currentDropdownPositionX;
      if (offsetPositionX < 0 && window.innerWidth < 768) {
        dropdown.style.left = `${offsetPositionX}px`;
      }
    } else {
      this.classList.remove('open');

      const buttonContainer = document.querySelector('sticky-add-to-cart').shadowRoot.querySelector('.button-container');
      if (buttonContainer) {
        buttonContainer.style.paddingTop = 0;
      }

      const dropdown = this.shadowRoot.querySelector('.select-options');
      dropdown.style.removeProperty('left');
    }
  }

  handleOptionClick(value, label, color) {
    // if (this.getOptionObject(value).disabled) {
    //   return;
    // }

    if (value === this.selectedValue) {
      this.toggleDropdown();
      return;
    }

    this.setSelectedLabel(label, color, value);
    this.selectedValue = value;
    this.updateDisplay();
    this.toggleDropdown();

    this.dispatchEvent(new CustomEvent('select-change', {
      bubbles: true,
      composed: true,
      detail: {
        optionType: this.optionType,
        value: value,
        label: label
      }
    }));
  }

  handleClickOutside(event) {
    if (this.isOpen && !event.composedPath().includes(this)) {
      this.isOpen = false;
      this.classList.remove('open');
    }
  }

  updateDisplay() {
    const valueElement = this.shadowRoot.querySelector('.select-value');

    if (valueElement) {
      if (typeof this.selectedLabel === 'string' || typeof this.selectedLabel === 'number') {
        valueElement.textContent = this.selectedLabel || '';
        valueElement.setAttribute('data-value', this.selectedValue);
      } else if (this.selectedLabel instanceof HTMLElement) {
        valueElement.innerHTML = '';
        valueElement.appendChild(this.selectedLabel);
      }
    }

    const options = this.shadowRoot.querySelectorAll('.select-option');
    options.forEach(option => {
      if (option.dataset.value === this.selectedValue) {
        option.classList.add('selected');
      } else {
        option.classList.remove('selected');
      }

      if (this.getOptionObject(option.dataset.value).disabled) {
        option.classList.add('disabled');
      } else {
        option.classList.remove('disabled');
      }
    });
  }

  setSelection(value) {
    const option = this.options.find(opt => opt.value === value);
    if (option && this.selectedValue !== value) {
      this.setSelectedLabel(option.label, option.color, value);
      this.selectedValue = value;
      this.updateDisplay();
    }
  }

  setDisability(value, disabled) {
    const option = this.getOptionObject(value);
    if (option) {
      option.disabled = disabled;
    }

    this.updateDisplay();
  }

  renderColorBox(color, label) {
    const colorBox = document.createElement('span');
    colorBox.classList.add('color-box');
    colorBox.style.backgroundColor = color;

    const colorLabel = document.createElement('span');
    colorLabel.classList.add('color-label');
    colorLabel.textContent = label;

    const colorWrapper = document.createElement('div');
    colorWrapper.classList.add('color-wrapper');

    colorWrapper.appendChild(colorBox);
    colorWrapper.appendChild(colorLabel);

    return colorWrapper;
  }

  convertSizes(value) {
    this.valueMod = value == 'US' ? parseInt(-36, 10) : 0;

    if (this.shadowRoot.querySelector('[data-size-type-container]')) {  
      this.shadowRoot.querySelector('[data-size-type-container]').textContent = `(${value})`
    }

    this.shadowRoot.querySelectorAll(".select-option").forEach((option) => {
      const value = option.dataset.value;
      const renderValue = parseInt(value, 10) + this.valueMod
      option.textContent = renderValue;
    });

    const selectValueElement = this.shadowRoot.querySelector('.select-value');
    const selectValue = selectValueElement?.dataset?.value;
    
    if (!selectValue) return;
    const renderValue = parseInt(selectValue, 10) + this.valueMod
    selectValueElement.textContent = renderValue;
  }

  unselectAll() {
    this.shadowRoot.querySelectorAll('.select-option').forEach(option => {
      option.classList.remove('selected');
    });

    this.selectedValue = '';
    this.selectedLabel = '';

    this.updateDisplay();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleClickOutside);
  }
}

customElements.define('custom-select', CustomSelect);